import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, BenefitsGrid, FAQSection, CTASection } from '../../components/PageTemplate';
import { 
  DollarSign, FileText, Clock, CheckCircle, CreditCard, 
  Shield, BarChart, AlertTriangle 
} from 'lucide-react';

export default function ContractorsPayments() {
  const features = [
    {
      icon: FileText,
      title: 'Easy Quoting',
      description: 'Submit quotes through the app. Include labour, materials, and any extras.'
    },
    {
      icon: DollarSign,
      title: 'Fair Rates',
      description: 'You set your rates. Landlords choose based on price, rating, and availability.'
    },
    {
      icon: CheckCircle,
      title: 'Pre-Approved Budgets',
      description: 'Many jobs have pre-approved budgets. Within budget = instant approval.'
    },
    {
      icon: CreditCard,
      title: 'Guaranteed Payment',
      description: 'Approved work is paid within 14 days. No chasing, no excuses.'
    },
    {
      icon: Clock,
      title: 'Fast Processing',
      description: 'Submit invoice, tenant confirms completion, payment processed automatically.'
    },
    {
      icon: BarChart,
      title: 'Income Tracking',
      description: 'See all your earnings, pending payments, and payment history in one place.'
    }
  ];

  const paymentProcess = [
    { step: 'Job Completed', description: 'You finish the work and upload photos' },
    { step: 'Tenant Confirms', description: 'Tenant confirms the work is done satisfactorily' },
    { step: 'Invoice Submitted', description: 'Submit your invoice through the app' },
    { step: 'Invoice Approved', description: 'Landlord or system approves the invoice' },
    { step: 'Payment Processed', description: 'Payment sent within 14 days' },
  ];

  const faqs = [
    {
      question: 'How do I submit a quote?',
      answer: 'When you accept a job, you can submit a quote through the app. Include: labour (hours x rate), materials (with receipts or estimates), call-out fee if applicable, and VAT if registered.'
    },
    {
      question: 'What if my quote is rejected?',
      answer: 'The landlord may ask for a revised quote or choose another contractor. You can adjust and resubmit, or withdraw from the job.'
    },
    {
      question: 'When exactly do I get paid?',
      answer: 'Within 14 days of invoice approval. Payments are made to your registered bank account every Friday for approved invoices.'
    },
    {
      question: 'What\'s the commission rate?',
      answer: '10-15% depending on job type and volume. High-volume contractors may qualify for reduced rates. The commission is deducted from your payment.'
    },
    {
      question: 'What if the tenant disputes the work?',
      answer: 'Payment is paused until the dispute is resolved. Provide your evidence (photos, messages) and we\'ll mediate. Fair disputes are resolved within 7 days.'
    },
    {
      question: 'Do I need to be VAT registered?',
      answer: 'Only if your turnover exceeds the VAT threshold. If registered, add VAT to your invoices. If not, submit net amounts.'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="PAYMENTS"
        title="Get Paid Fairly,"
        subtitle="Get Paid Fast"
        description="Set your rates, submit quotes easily, and get guaranteed payment within 14 days of job completion."
        primaryCTA={{ href: '/contractor/register', label: 'Join Network' }}
        secondaryCTA={{ href: '/contractors', label: 'Back to Overview' }}
        gradient="from-green-50 via-white to-slate-50"
      />

      <BenefitsGrid title="Payment Features" benefits={features} />

      {/* Payment Process */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8">
            Payment Timeline
          </h2>
          <div className="space-y-4">
            {paymentProcess.map((item, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                  index === paymentProcess.length - 1 ? 'bg-green-500' : 'bg-primary'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1 p-4 bg-slate-50 rounded-xl">
                  <div className="font-semibold">{item.step}</div>
                  <div className="text-sm text-slate-600">{item.description}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-slate-500 mt-6">
            Total time from completion to payment: typically 5-14 days
          </p>
        </div>
      </section>

      {/* Commission Info */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8">
            Commission Structure
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="text-3xl font-bold text-primary mb-2">15%</div>
              <div className="font-semibold mb-2">Standard</div>
              <p className="text-sm text-slate-600">Less than 10 jobs/month</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center border-2 border-primary">
              <div className="text-3xl font-bold text-primary mb-2">12%</div>
              <div className="font-semibold mb-2">Active</div>
              <p className="text-sm text-slate-600">10-25 jobs/month</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="text-3xl font-bold text-primary mb-2">10%</div>
              <div className="font-semibold mb-2">Partner</div>
              <p className="text-sm text-slate-600">25+ jobs/month</p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <CTASection
        title="Ready to earn with LetWise?"
        description="Join our network and start receiving guaranteed-payment jobs."
        primaryCTA={{ href: '/contractor/register', label: 'Apply Now' }}
      />
    </PublicLayout>
  );
}
